export interface BusinessPartner {
    id?: number;
    name?: string;
    organizationId?: number;
    invitationType?: string;
    contactRole?: string;
    customValues?: Record<string, unknown>;
    state?: string;
    email?: string;
    gln?: string;
    address?: string;
    url?: string;
    imageUrl?: string;
    date?: number | string;
    deleted?: boolean;
    managedContact?: boolean;
}

export interface ContactGroup {
    letter: string;
    contacts: BusinessPartner[];
}

export interface UserOrganizationRef {
    id?: number;
    managingAccount?: boolean;
}

export interface ContactErrorBody {
    message?: string;
    defaultMessage?: string;
    parameters?: Record<string, string | number>;
}

const EMAIL_REGEXP = /^([\w-]+(?:\.[\w-]+)*)\+?[\w-]+@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,}(?:\.[a-z]{2})?)$/i;

const ESTABLISHED = 'ESTABLISHED';

export function establishedContacts(contacts: BusinessPartner[]): BusinessPartner[] {
    return contacts.filter((contact) => contact.state === ESTABLISHED);
}

/**
 * The search response includes a cursor even when the page is shorter than
 * the limit. Following that cursor returns an empty page, so a short page
 * is the last page.
 */
export function partnersNextCursor(rowCount: number, limit: number, cursor?: string): string | undefined {
    if (rowCount < limit) {
        return undefined;
    }
    return cursor || undefined;
}

/**
 * Groups established contacts by the first letter of the name, preserving
 * name order. Skips deleted contacts and contacts without a name.
 */
export function groupContactsByLetter(contacts: BusinessPartner[]): ContactGroup[] {
    const grouped = new Map<string, BusinessPartner[]>();

    contacts.forEach((contact) => {
        if (contact.deleted || !contact.name) {
            return;
        }
        const letter = contact.name.toUpperCase().substring(0, 1);
        const existing = grouped.get(letter);
        if (existing) {
            existing.push(contact);
        } else {
            grouped.set(letter, [contact]);
        }
    });

    return Array.from(grouped, ([letter, letterContacts]) => ({
        letter,
        contacts: letterContacts,
    }));
}

export function isContactEmailValid(email: string): boolean {
    if (!email) {
        return true;
    }
    return EMAIL_REGEXP.test(email);
}

export function hidesContactActions(organizationId: unknown, contactId: unknown): boolean {
    if (organizationId == null || contactId == null) {
        return false;
    }
    return String(organizationId) === String(contactId);
}

export function canShowDeleteContact(communityManager: boolean, invitationType?: string): boolean {
    return communityManager || invitationType === 'CONTACT_REQUEST';
}

export function canLoginAsManagingUser(input: {
    communityManager: boolean;
    legacyAccount: boolean;
    contact: BusinessPartner;
    userOrganizations: UserOrganizationRef[];
}): boolean {
    if (!input.communityManager || input.legacyAccount) {
        return false;
    }
    const { organizationId, invitationType } = input.contact;
    if (organizationId == null) {
        return false;
    }
    if (invitationType !== 'INVITED_BUYER' && invitationType !== 'INVITED_SUPPLIER') {
        return false;
    }
    const existing = input.userOrganizations.find((organization) => organization.id === organizationId);
    if (existing && !existing.managingAccount) {
        return false;
    }
    return true;
}

export function contactErrorMessage(
    data: ContactErrorBody | string | undefined,
    translate: (key: string, values?: Record<string, string | number>) => string,
    fallback?: string,
): string | undefined {
    if (typeof data === 'string' && data) {
        return data;
    }
    if (data && typeof data === 'object' && data.message) {
        const translated = translate(data.message, data.parameters);
        if (translated === data.message && data.defaultMessage) {
            return translate(data.defaultMessage, data.parameters);
        }
        return translated;
    }
    return fallback ? translate(fallback) : undefined;
}

export function partnersPageSize(): number {
    if (typeof window === 'undefined') {
        return 20;
    }
    return Math.max(1, Math.floor(window.innerHeight / 40));
}

export function formatContactDate(value: number | string | undefined, withTime: boolean): string {
    if (value == null || value === '') {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    if (withTime) {
        return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'medium' });
    }
    return date.toLocaleDateString(undefined, { dateStyle: 'medium' });
}
